import * as posActions from "../../lib/posActions";
import * as posSelectors from "../../lib/posSelectors";
import * as routeHelpers from "../../lib/routeHelpers";
import Checkbox from "../Checkbox";
import cn from "classnames";
import describeNetworkError from "../../lib/describeNetworkError";
import Drawer from "../Drawer";
import ErrorModal from "../ErrorModal";
import logger from "../../lib/sentryLogger";
import ManagerPermissionForm from "../ManagerPermissionForm";
import OutlineButton from "../OutlineButton";
import React, {useState, useEffect} from "react";
import Spinner from "../Spinner";
import styles from "./OrderSubtotalLayout.module.css";
import TextModal from "../TextModal";
import trashIcon from "../../images/trash.svg";
import {Actions, API, Lib} from "habit-core";
import {
    Outlet,
    useOutletContext,
    useLocation,
    useNavigate,
} from "react-router-dom";
import {useAppDispatch, useAppSelector} from "../../lib/hooks";

const strings = {
    subtotal: "Subtotal :",
    tax: "Tax :",
    tip: "Tip :",
    orderTotal: "Order Total :",
    balanceDue: "Balance Due :",
    charity: "Charity :",
    cashPaid: "Cash :",
    changeOwed: "Change Owed :",
    discountApplied: "Discount Applied: ",
    removeDiscount: "Remove Discount",
    giftCard: "Gift Card :",
    compCard: "Comp Card :",
    balance: "Balance :",
    creditCard: (last4?: string) => {
        return last4 ? `Credit Card -${last4}` : "Credit Card";
    },
    removeGiftCard: "Remove Gift Card",
    removeCompCard: "Remove Comp Card",
    giftCardProcessed: "Card Successfully Processed.",
    errorAfterPaymentsProcessedMessage: (orderId: string) =>
        `Payment was successfully captured, but this order failed to process and was not sent to the kitchen. Please note the order contents and send them manually to the kitchen.\n\nOrder Id: ${orderId}`,
    placeOrder: "Place Order",
    nextOrder: "Next Order",
    proceed: "Proceed",
    fundraiserApplied: "Fundraiser Applied",
    removeFundraiser: "Remove Fundraiser",
    ezCater: "Ez Cater",
    printGuestReceipt: "Print Guest Receipt",
    cancelOrder: "Cancel Order",
    confirm: "Confirmation",
    confirmText: "Are you sure you want to cancel this order?",
    goBack: "Go Back",
    submit: "Cancel This Order",
    cancelOrderErrorMessageGeneric:
        "An unexpected error occurred when canceling the current order. Please try again.",
};

type PaidContext = {
    tempDiscountCents: API.models.USDCents;
    setTempDiscountCents: (discountAmountCents: API.models.USDCents) => void;
};
export function usePaidContext() {
    return useOutletContext<PaidContext>();
}

export default function OrderSubtotalLayout() {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    const location = useLocation();
    const isOtherOrdersPage = location.pathname.includes(
        routeHelpers.otherOrders(),
    );
    const isCheckoutGiftCardPurchasePage = location.pathname.includes(
        routeHelpers.giftCardPurchase(),
    );

    const autoPrintReceipts = useAppSelector(
        (state) => state.pos.station.autoPrintReceipts,
    );
    const currentOrderPrintReceipt = useAppSelector(
        (state) => state.pos.currentOrder.printReceipt,
    );
    const posCurrentOrder = useAppSelector((state) => state.pos.currentOrder);

    const isSubmittingOrder = useAppSelector(
        (state) => state.pos.station.isSubmittingOrder,
    );
    const [isCancelingOrder, setIsCancelingOrder] = useState(false);
    const placeOrderAfterPaymentsProcessedErrored = useAppSelector(
        (state) => state.pos.errors.placeOrderAfterPaymentsProcessedErrored,
    );
    const currentOrderPlacementErrorMessage = useAppSelector(
        (state) => state.pos.errors.currentOrderPlacmentErrorMessage,
    );
    const currentOrderIsSubmitted = useAppSelector(
        (state) => state.pos.currentOrder.isSubmitted,
    );
    const giftCardsToPurchase = posCurrentOrder.giftCards.purchase;
    const giftCardNumbersRequired = giftCardsToPurchase.length > 0;
    const latestPaymentCoveredBalance = useAppSelector(
        (state) => state.pos.currentOrder.havePaymentsCoveredBalance,
    );

    const cashPaidCents = useAppSelector(
        (state) => state.pos.currentOrder.payment.cashPaidCents,
    );
    const changeOwedCents = useAppSelector(
        (state) => state.pos.currentOrder.payment.changeOwedCents,
    );
    const giftCardsApplied = useAppSelector(
        (state) => state.pos.currentOrder.payment.giftCardsApplied,
    );
    const compCardsApplied = useAppSelector(
        (state) => state.pos.currentOrder.payment.compCardsApplied,
    );
    const compCardPaidCents = compCardsApplied.reduce(
        (sum, cc) => (cc.appliedCents ?? 0) + sum,
        0,
    );
    const creditCardsApplied = useAppSelector(
        (state) => state.pos.currentOrder.payment.creditCardsApplied,
    );
    const staffTipCents: API.models.USDCents = creditCardsApplied.reduce(
        (sum, creditCard) => creditCard.tipAmountCents + sum,
        0,
    );
    const ezCaterApplied = posCurrentOrder.payment.ezCaterApplied;
    const externalTendersApplied = useAppSelector(
        (state) => state.pos.currentOrder.payment.externalTendersApplied,
    );
    const fundraiserCodeApplied =
        useAppSelector((state) => state.currentOrder.fundraiserCode) !== null;

    const paidCents = useAppSelector((state) =>
        posSelectors.getCurrentOrderPaidCents(state),
    );

    const balanceDueCents = useAppSelector((state) =>
        posSelectors.getCurrentOrderBalanceDueCents(state),
    );

    const subtotalCents = useAppSelector((state) =>
        posSelectors.getCurrentOrderSubtotalSansVoidedCents(state),
    );

    const appliedCharityAmountCents = useAppSelector((state) =>
        posSelectors.getCurrentOrderAppliedCharityAmountCents(state),
    );
    const taxCents = useAppSelector((state) =>
        posSelectors.getCurrentOrderTaxSansVoidedCents(state, compCardsApplied),
    );
    const orderTotalCents =
        subtotalCents + appliedCharityAmountCents + taxCents;
    const discounts = useAppSelector(
        (state) => state.pos.currentOrder.discounts,
    );
    const discountAmountCents = Math.min(
        useAppSelector((state) =>
            posSelectors.getCurrentOrderDiscountAmountCents(state),
        ),
        subtotalCents,
    );
    const [tempDiscountCents, setTempDiscountCents] = useState(0);

    const currentOrderEmpty = useAppSelector(
        (state) =>
            !state.currentOrder.comboCustomizationIds.length &&
            !state.currentOrder.itemCustomizationIds.length &&
            !state.pos.currentOrder.giftCards.purchase.length &&
            !state.pos.currentOrder.giftCards.addFunds.length,
    );

    const [showCancelCurrentOrderWorkflow, setShowCancelCurrentOrderWorkflow] =
        useState(false);
    const [cancelCurrentOrderErrorMessage, setCancelCurrentOrderErrorMessage] =
        useState("");

    const cashier = useAppSelector((state) => state.pos.station.cashier);
    const [managerId, setManagerId] = useState(
        cashier.type === "manager" ? cashier.managerId : null,
    );

    useEffect(() => {
        if (cashier.type !== "manager") {
            setManagerId(null);
        }
    }, [cashier.type]);

    // TODO: do we want to move this elsewhere?
    useEffect(() => {
        if (currentOrderIsSubmitted) {
            let header: string | undefined = undefined;
            if (location.pathname === routeHelpers.giftCardPayment()) {
                header = strings.giftCardProcessed;
            }

            navigate(routeHelpers.orderComplete(), {
                state: {
                    header,
                },
            });
        } else if (latestPaymentCoveredBalance && giftCardNumbersRequired) {
            navigate(routeHelpers.giftCardPurchase());
        }
    }, [
        currentOrderIsSubmitted,
        giftCardNumbersRequired,
        latestPaymentCoveredBalance,
    ]);

    const onSubmitFullyCoveredOrder = () => {
        if (discountAmountCents < orderTotalCents) {
            return;
        }

        dispatch(posActions.setResetCurrentOrderStateOnCartEmptied(false));
        dispatch(posActions.reserveCurrentOrderRefNumIfNeeded())
            .then(() => {
                dispatch(posActions.placeCurrentOrder("paid", []))
                    .then(() => {
                        // TODO: #73 - Pass correct params depending on order / payment type
                        navigate(routeHelpers.orderComplete());
                    })
                    .catch((err) => {
                        logger.error("Failed to place fully covered order.", {
                            orderId: posCurrentOrder.id,
                        });
                        dispatch(
                            posActions.setCurrentOrderPlacementErrorMessage(
                                describeNetworkError(err).join("\n"),
                            ),
                        );
                    });
            })
            .catch((err) => {
                logger.error(
                    "Failed to place order. Could not reserve reference number.",
                    {
                        orderId: posCurrentOrder.id,
                    },
                );
                // TODO: if this errors, how to recover?
                dispatch(
                    posActions.setCurrentOrderPlacementErrorMessage(
                        describeNetworkError(err).join("\n"),
                    ),
                );
            });
    };

    const clearAndStartNewOrder = () => {
        dispatch(posActions.startNewOrder()).then(() => {
            navigate(routeHelpers.menu());
        });
    };

    const getManualProceedButton = () => {
        if (
            (balanceDueCents ||
                currentOrderEmpty ||
                isCheckoutGiftCardPurchasePage ||
                currentOrderIsSubmitted) &&
            !placeOrderAfterPaymentsProcessedErrored
        ) {
            return null;
        }

        let label = strings.nextOrder;
        let onClick: () => void = clearAndStartNewOrder;

        if (!placeOrderAfterPaymentsProcessedErrored) {
            label = strings.placeOrder;
            if (giftCardNumbersRequired && balanceDueCents <= 0) {
                onClick = () => navigate(routeHelpers.giftCardPurchase());
                label = strings.proceed;
            } else if (discountAmountCents <= orderTotalCents) {
                onClick = () =>
                    dispatch(posActions.processPaymentAndPlaceCurrentOrder());
            } else if (discountAmountCents > orderTotalCents) {
                onClick = onSubmitFullyCoveredOrder;
            }
        }

        return (
            <OutlineButton
                label={label}
                onClick={onClick}
                loading={isSubmittingOrder}
            />
        );
    };

    const onCancelCurrentOrder = () => {
        if (isCancelingOrder || !managerId) {
            return;
        }

        setIsCancelingOrder(true);
        dispatch(posActions.cancelCurrentOrder(managerId))
            .then(() => {
                dispatch(posActions.startNewOrder());
                navigate(routeHelpers.menu());
            })
            .catch((err) => {
                logger.error(
                    "Failed to cancel current order after partial cc payment applied.",
                    {
                        orderId: posCurrentOrder.id,
                        errorMessage: describeNetworkError(err).join("\n"),
                    },
                );
                setCancelCurrentOrderErrorMessage(
                    strings.cancelOrderErrorMessageGeneric,
                );
            })
            .finally(() => {
                setIsCancelingOrder(false);
            });
    };

    const getCancelButton = () => {
        if (
            creditCardsApplied.length === 0 ||
            isSubmittingOrder ||
            currentOrderIsSubmitted
        ) {
            return null;
        }

        return (
            <OutlineButton
                label={strings.cancelOrder}
                className={styles.cancelCurrentOrderButton}
                onClick={() => setShowCancelCurrentOrderWorkflow(true)}
                loading={isCancelingOrder}
            />
        );
    };

    const getRightContent = () => {
        if (isOtherOrdersPage) {
            return <div className={cn(styles.bordered, styles.right)} />;
        }

        return (
            <div className={styles.checkoutRightContainer}>
                <div className={cn(styles.bordered, styles.right)}>
                    <div className={styles.row}>
                        <span>{strings.subtotal}</span>
                        <span>
                            {Lib.currency.centsToDollarString(
                                Math.max(
                                    0,
                                    subtotalCents -
                                        discountAmountCents -
                                        compCardPaidCents,
                                ),
                            )}
                        </span>
                    </div>

                    <div className={styles.row}>
                        <span>{strings.tax}</span>
                        <span>
                            {Lib.currency.centsToDollarString(taxCents)}
                        </span>
                    </div>

                    {staffTipCents ? (
                        <div className={styles.row}>
                            <span>{strings.tip}</span>
                            <span>
                                {Lib.currency.centsToDollarString(
                                    staffTipCents,
                                )}
                            </span>
                        </div>
                    ) : null}

                    {appliedCharityAmountCents > 0 ? (
                        <div className={styles.row}>
                            <span>{strings.charity}</span>
                            <span>
                                {Lib.currency.centsToDollarString(
                                    appliedCharityAmountCents,
                                )}
                            </span>
                        </div>
                    ) : null}

                    <div className={styles.row}>
                        <span>{strings.orderTotal}</span>
                        <span>
                            {Lib.currency.centsToDollarString(
                                orderTotalCents + (staffTipCents ?? 0),
                            )}
                        </span>
                    </div>

                    <div className={styles.spacer} />
                    {cashPaidCents !== 0 ? (
                        <div className={styles.row}>
                            <span>{strings.cashPaid}</span>
                            <span className={styles.cashRight}>
                                <span>
                                    (
                                    {Lib.currency.centsToDollarString(
                                        cashPaidCents,
                                    )}
                                    )
                                </span>
                                {location.pathname ===
                                routeHelpers.orderComplete() ? null : (
                                    <button
                                        className={styles.trashButton}
                                        onClick={() => {
                                            dispatch(
                                                posActions.setPaymentCashPaidCents(
                                                    0,
                                                ),
                                            );
                                            dispatch(
                                                posActions.setPaymentChangeOwedCents(
                                                    0,
                                                ),
                                            );
                                        }}
                                        disabled={
                                            isSubmittingOrder ||
                                            currentOrderIsSubmitted ||
                                            placeOrderAfterPaymentsProcessedErrored ||
                                            isCheckoutGiftCardPurchasePage
                                        }>
                                        <img src={trashIcon} alt="" />
                                    </button>
                                )}
                            </span>
                        </div>
                    ) : null}
                    {creditCardsApplied.length ? (
                        <>
                            <div className={styles.spacer} />
                            {creditCardsApplied.map((ccApplied, i) => {
                                return (
                                    <div key={`cc-${i}`} className={styles.row}>
                                        <span>
                                            {strings.creditCard(
                                                ccApplied.cardNumber?.slice(-4),
                                            )}
                                        </span>
                                        <span className={styles.cashRight}>
                                            <span>
                                                (
                                                {Lib.currency.centsToDollarString(
                                                    ccApplied.appliedAmountCents,
                                                )}
                                                )
                                            </span>
                                        </span>
                                    </div>
                                );
                            })}
                        </>
                    ) : null}
                    {ezCaterApplied ? (
                        <div className={styles.row}>
                            <span>{strings.ezCater}</span>
                            <span className={styles.cashRight}>
                                <span>
                                    (
                                    {Lib.currency.centsToDollarString(
                                        paidCents - discountAmountCents,
                                    )}
                                    )
                                </span>
                            </span>
                        </div>
                    ) : null}
                    {tempDiscountCents ? (
                        <>
                            <div className={styles.spacer} />
                            <div className={styles.discountRow}>
                                <div className={styles.row}>
                                    <span>{strings.discountApplied}</span>
                                    <span>
                                        {Lib.currency.centsToDollarString(
                                            -tempDiscountCents,
                                        )}
                                    </span>
                                </div>
                            </div>
                        </>
                    ) : null}
                    {discounts.length ? (
                        <>
                            <div className={styles.spacer} />
                            {discounts.map((discount, i) => {
                                const discountAmount = discount.amountCents
                                    ? discount.amountCents
                                    : Lib.currency.roundFloat(
                                          (subtotalCents *
                                              (discount.amountPercentage ??
                                                  0)) /
                                              100.0,
                                      );
                                return (
                                    <div key={i} className={styles.discountRow}>
                                        <div className={styles.row}>
                                            <span>
                                                {discount.name ??
                                                    strings.discountApplied}
                                            </span>
                                            <span>
                                                {Lib.currency.centsToDollarString(
                                                    -discountAmount,
                                                )}
                                            </span>
                                        </div>
                                        <button
                                            className={styles.removeDiscount}
                                            onClick={() => {
                                                logger.info(
                                                    `Removing discount with code ${discount.discountCode}`,
                                                    {
                                                        orderId:
                                                            posCurrentOrder.id,
                                                    },
                                                );
                                                dispatch(
                                                    posActions.currentOrderRemoveDiscount(
                                                        discount.discountCode,
                                                        discount.rewardId,
                                                    ),
                                                );
                                            }}
                                            disabled={
                                                isSubmittingOrder ||
                                                currentOrderIsSubmitted
                                            }>
                                            {strings.removeDiscount}
                                        </button>
                                    </div>
                                );
                            })}
                        </>
                    ) : null}
                    {giftCardsApplied.length
                        ? giftCardsApplied.map((gc) => (
                              <div key={gc.id} className={styles.gcSection}>
                                  <div className={cn(styles.row, styles.gcRow)}>
                                      <span>{strings.giftCard}</span>
                                      <span>
                                          (
                                          {Lib.currency.centsToDollarString(
                                              gc.appliedCents,
                                          )}
                                          )
                                      </span>
                                  </div>
                                  <div className={cn(styles.gcBalanceRow)}>
                                      <span>{strings.balance}</span>
                                      <span>
                                          {Lib.currency.centsToDollarString(
                                              gc.balanceRemainingCents,
                                          )}
                                      </span>
                                  </div>
                                  <button
                                      className={styles.removeGiftCardButton}
                                      onClick={() => {
                                          logger.info(
                                              `Removing gift card with number ${gc.id}`,
                                              {
                                                  orderId: posCurrentOrder.id,
                                              },
                                          );
                                          dispatch(
                                              posActions.removePaymentGiftCard(
                                                  gc.id,
                                              ),
                                          );
                                      }}
                                      disabled={
                                          isSubmittingOrder ||
                                          currentOrderIsSubmitted
                                      }>
                                      {strings.removeGiftCard}
                                  </button>
                              </div>
                          ))
                        : null}
                    {compCardsApplied
                        .filter((cc) => !!cc.appliedCents)
                        .map((cc) => (
                            <div key={cc.id} className={styles.gcSection}>
                                <div className={cn(styles.row, styles.gcRow)}>
                                    <span>{strings.compCard}</span>
                                    <span>
                                        (
                                        {Lib.currency.centsToDollarString(
                                            cc.appliedCents ?? 0,
                                        )}
                                        )
                                    </span>
                                </div>
                                <div className={cn(styles.gcBalanceRow)}>
                                    <span>{strings.balance}</span>
                                    <span>
                                        {Lib.currency.centsToDollarString(
                                            cc.balance - (cc.appliedCents ?? 0),
                                        )}
                                    </span>
                                </div>
                                <button
                                    className={styles.removeGiftCardButton}
                                    onClick={() => {
                                        logger.info(
                                            `Removing comp card with number ${cc.id}`,
                                            {
                                                orderId: posCurrentOrder.id,
                                            },
                                        );
                                        dispatch(
                                            posActions.removePaymentCompCard(
                                                cc.id,
                                            ),
                                        );
                                    }}
                                    disabled={
                                        isSubmittingOrder ||
                                        currentOrderIsSubmitted
                                    }>
                                    {strings.removeCompCard}
                                </button>
                            </div>
                        ))}
                    {externalTendersApplied
                        .filter((tender) => !!tender.appliedCents)
                        .map((tender, i) => (
                            <div key={i} className={styles.row}>
                                <span>{tender.tenderName}</span>
                                <span className={styles.cashRight}>
                                    <span>
                                        (
                                        {Lib.currency.centsToDollarString(
                                            tender.appliedCents ?? 0,
                                        )}
                                        )
                                    </span>
                                    {location.pathname ===
                                    routeHelpers.orderComplete() ? null : (
                                        <button
                                            className={styles.trashButton}
                                            onClick={() => {
                                                dispatch(
                                                    posActions.removeExternalTender(
                                                        tender.tenderName,
                                                    ),
                                                );
                                            }}
                                            disabled={
                                                isSubmittingOrder ||
                                                currentOrderIsSubmitted ||
                                                placeOrderAfterPaymentsProcessedErrored ||
                                                isCheckoutGiftCardPurchasePage
                                            }>
                                            <img src={trashIcon} alt="" />
                                        </button>
                                    )}
                                </span>
                            </div>
                        ))}
                    {fundraiserCodeApplied ? (
                        <>
                            <div className={styles.spacer} />
                            <div className={styles.fundraiserRow}>
                                <div>{strings.fundraiserApplied}</div>
                                <button
                                    className={styles.removeDiscount}
                                    onClick={() =>
                                        dispatch(
                                            Actions.currentOrderActions.removeFundraiserCode(),
                                        )
                                    }
                                    disabled={
                                        isSubmittingOrder ||
                                        currentOrderIsSubmitted ||
                                        isCheckoutGiftCardPurchasePage
                                    }>
                                    {strings.removeFundraiser}
                                </button>
                            </div>
                        </>
                    ) : null}

                    <div className={styles.divider} />

                    <div
                        className={cn(
                            balanceDueCents > 0
                                ? styles.balanceDueContainer
                                : styles.row,
                            styles.large,
                        )}>
                        <span>{strings.balanceDue}</span>
                        <span
                            className={
                                balanceDueCents > 0
                                    ? styles.balanceDueAmount
                                    : undefined
                            }>
                            {Lib.currency.centsToDollarString(
                                tempDiscountCents
                                    ? Math.max(
                                          balanceDueCents - tempDiscountCents,
                                          0,
                                      )
                                    : balanceDueCents,
                            )}
                        </span>
                    </div>
                    {/* TODO: replace this once we have updated styles */}
                    {getManualProceedButton()}
                    {getCancelButton()}
                    {changeOwedCents !== 0 ? (
                        <div
                            className={cn(
                                styles.changeContainer,
                                styles.change,
                            )}>
                            <span>{strings.changeOwed}</span>
                            <span className={styles.changeAmount}>
                                {Lib.currency.centsToDollarString(
                                    changeOwedCents,
                                )}
                            </span>
                        </div>
                    ) : null}
                </div>
                {autoPrintReceipts ? (
                    <Checkbox
                        label={strings.printGuestReceipt}
                        checked={currentOrderPrintReceipt}
                        onChange={(e) =>
                            dispatch(
                                posActions.setCurrentOrderPrintReceipt(
                                    e.target.checked,
                                ),
                            )
                        }
                        disabled={
                            isSubmittingOrder ||
                            location.pathname === routeHelpers.orderComplete()
                        }
                    />
                ) : null}
            </div>
        );
    };

    return (
        <>
            <div className={cn(styles.bordered, styles.left)}>
                {isSubmittingOrder ? (
                    <div className={styles.spinner}>
                        <Spinner />
                    </div>
                ) : (
                    <Outlet
                        context={{
                            tempDiscountCents,
                            setTempDiscountCents,
                        }}
                    />
                )}
            </div>

            {getRightContent()}

            {cancelCurrentOrderErrorMessage ? (
                <ErrorModal
                    errorMessage={cancelCurrentOrderErrorMessage}
                    onClose={() => setCancelCurrentOrderErrorMessage("")}
                    showITInfo
                />
            ) : null}
            {showCancelCurrentOrderWorkflow && managerId ? (
                <TextModal
                    onClose={() => {
                        if (cashier.type !== "manager") {
                            setManagerId(null);
                        }
                        setShowCancelCurrentOrderWorkflow(false);
                    }}
                    title={strings.confirm}
                    content={strings.confirmText}
                    cancelLabel={strings.goBack}
                    onSubmit={onCancelCurrentOrder}
                    submitLabel={strings.submit}
                    submitLoading={isCancelingOrder}
                    submitMode="red"
                />
            ) : null}
            {showCancelCurrentOrderWorkflow && !managerId ? (
                <Drawer
                    size="large"
                    onClose={() => {
                        setShowCancelCurrentOrderWorkflow(false);
                    }}>
                    <div className={styles.managerPermissionFormContainer}>
                        <ManagerPermissionForm
                            onSuccess={(data) => setManagerId(data.id)}
                        />
                    </div>
                </Drawer>
            ) : null}

            {currentOrderPlacementErrorMessage ? (
                <ErrorModal
                    errorMessage={currentOrderPlacementErrorMessage}
                    onClose={() =>
                        dispatch(
                            posActions.setCurrentOrderPlacementErrorMessage(
                                undefined,
                            ),
                        )
                    }
                    showITInfo
                />
            ) : null}
        </>
    );
}
